/* eslint-disable camelcase */
import config from 'config';
import i18n from '@/i18n';
import { authHeader } from '@/services/authentication';
import axios from '@/util/axios';
import { ISSUE_PRIORITY, ISSUE_STATUS } from '@/constants';

const issuesURL = `${config.apiUrl}/${config.apiVersion}/issues`;

export const encodeFilters = ({
    assignees = null,
    priority = null,
    status = [ISSUE_STATUS.SOLVED],
    sortBy = null,
    deadline = null,
    noAssignee = 0,
    search = null,
    from = null,
    to = null,
}) => ({
    assignees,
    priority,
    status,
    sortBy,
    deadline,
    noAssignee: noAssignee ? 1 : 0,
    search,
    from,
    to,
});

export const encodePhotos = (photos = []) => photos.map(({
    uuid = null,
    width = null,
    height = null,
    drawing = null,
    image = null,
    thumbnail = null,
} = {}) => ({
    uuid,
    width,
    height,
    drawing,
    image,
    thumbnail,
}));

export const encodeIssue = ({
    title = null,
    site = null,
    assignees = [],
    deadline = null,
    description = null,
    priority = null,
    suggestedCorrectiveAction = null,
    performedCorrectiveAction = null,
    status = null,
    photos = [],

}, id) => {
    if (title === null) throw new Error(i18n.t('Encode issue: Failed to encode title {title}', { title }));
    if (id && status === null) throw new Error(i18n.t('Encode issue: Failed to encode status, please provide a value when trying to edit'));
    return {
        title,
        site,
        description,
        suggested_corrective_action: suggestedCorrectiveAction,
        assignees: (assignees && assignees.length > 0) ? assignees : null,
        deadline: deadline || null,
        priority: {
            [ISSUE_PRIORITY.LOW]: ISSUE_PRIORITY.LOW,
            [ISSUE_PRIORITY.MEDIUM]: ISSUE_PRIORITY.MEDIUM,
            [ISSUE_PRIORITY.HIGH]: ISSUE_PRIORITY.HIGH,
        }[priority],
        performed_corrective_action: performedCorrectiveAction,
        status,
        photos: encodePhotos(photos),
    };
};

export const decodeIssue = ({
    id,
    todo_id,
    user_id,
    organization_id,
    site_id,
    title,
    description,
    suggested_corrective_action,
    performed_corrective_action,
    created_at,
    updated_at,
    deleted_at,
    resolved_at,
    resolved_by,
    edited_by_user_id,
    status,
    priority,
    payload,
    sites,
    due_at,
    assignees = {},
    sensorsLastMeasurement = null,
    photos = [],
}) => ({
    id,
    todoId: todo_id,
    userId: user_id,
    organizationId: organization_id,
    siteId: site_id,
    title,
    description,
    suggestedCorrectiveAction: suggested_corrective_action,
    performedCorrectiveAction: performed_corrective_action,
    createdAt: created_at,
    updatedAt: updated_at,
    deletedAt: deleted_at,
    resolvedAt: resolved_at,
    resolvedBy: resolved_by,
    editedByUserId: edited_by_user_id,
    status,
    priority,
    payload,
    sites,
    dueAt: due_at,
    assignees,
    sensorsLastMeasurement,
    photos,
});

export const decodeIssues = ({
    next_page_url,
    prev_page_url,
    resources = [],
}) => ({
    nextPageUrl: next_page_url,
    prevPageUrl: prev_page_url,
    resources: resources.map(decodeIssue),
});

const getAll = (
    url = issuesURL,
    filters = {},
) => axios.get(url, {
    headers: authHeader(),
    params: encodeFilters(filters),
}).then((response) => decodeIssues(response.data));

const get = (
    id = null,
    url = issuesURL,
) => {
    if (!id) throw new Error(i18n.t('You must provide an ID in order to fetch an issue'));
    return axios.get(
        `${url}/${id}`,
        { headers: authHeader() },
    ).then((response) => decodeIssue(response.data));
};

const getFilterOptions = () => axios.get(
    `${issuesURL}/filters`,
    { headers: authHeader() },
).then((response) => response.data);

const raise = (payload = {}) => axios.post(issuesURL,
    encodeIssue(payload),
    { headers: authHeader() }).then((response) => response.data);

const edit = (id, payload = {}) => {
    if (!id) throw new Error(i18n.t('You must provide an ID in order to edit an issue'));
    return axios.post(`${issuesURL}/${id}`,
        {
            ...encodeIssue(payload, id),
            _method: 'PUT',
        },
        { headers: authHeader() }).then((response) => response.data);
};

export default {
    get,
    getAll,
    getFilterOptions,
    raise,
    edit,
};
